import type { GetContextMenuItemsParams } from "@/ui/molecules/Grid";
import type { TAllRowsAvailable } from "../typesGrids";
import { isNullable } from "@/utils/guards";
import { isRowCategory, isRowTotal } from "../typeChecksGrids";

// Redefine TRowType as in GridMappings.tsx
export type TRowType = "account" | "empty" | "category" | "total";

// Helper for account row type
const isAccountRowType = (row: TAllRowsAvailable) => "account" in row;

export function getGridMappingsContextMenuItems(
    params: GetContextMenuItemsParams<TAllRowsAvailable>,
    onChangeRowType: (params: { rowId: number; rowType: TRowType }) => void,
    onFlipRowSign: (params: { rowId: number }) => void,
    onSetDocumentTargetRow: (params: { rowId: number | null }) => void,
) {
    if (isNullable(params.node) || isNullable(params.node.data)) {
        return [];
    }
    const rowData = params.node.data;
    const isNOI =
        rowData.rowId === "row-original" || rowData.rowId === "row-altered";
    let currentType: TRowType | null = null;
    if (isAccountRowType(rowData)) {
        currentType = "account";
    } else if (isRowCategory(rowData)) {
        currentType = "category";
    } else if (isRowTotal(rowData) && !isNOI) {
        currentType = "total";
    }
    const isValueRow =
        currentType === "account" || (currentType === "total" && !isNOI);
    const allRowTypes = [
        { name: "Account", value: "account" as const },
        { name: "Category", value: "category" as const },
        { name: "Total", value: "total" as const },
    ];
    const changeRowTypeSubMenu = currentType
        ? {
              name: "Change Row Type",
              subMenu: allRowTypes.map(option =>
                  option.value === currentType
                      ? {
                            name: `Change to ${option.name}`,
                            checked: true,
                        }
                      : {
                            name: `Change to ${option.name}`,
                            action: () => {
                                if (params.node && params.node.data) {
                                    onChangeRowType({
                                        rowId: Number(params.node.data.rowId),
                                        rowType: option.value,
                                    });
                                }
                            },
                        },
              ),
          }
        : null;
    const flipRowSignMenuItem = isValueRow
        ? {
              name: "Flip Sign (+/-)",
              action: () => {
                  if (params.node && params.node.data) {
                      onFlipRowSign({
                          rowId: Number(params.node.data.rowId),
                      });
                  }
              },
          }
        : null;
    const isDocumentTargetRow =
        isRowTotal(rowData) && rowData.isDocumentTarget === true;
    const setDocumentTargetMenuItem =
        currentType === "total" && !isNOI && !isDocumentTargetRow
            ? {
                  name: "Set as document target",
                  action: () => {
                      if (params.node && params.node.data) {
                          onSetDocumentTargetRow({
                              rowId: Number(params.node.data.rowId),
                          });
                      }
                  },
              }
            : null;
    const resetDocumentTargetMenuItem =
        currentType === "total" && isDocumentTargetRow
            ? {
                  name: "Reset to original",
                  action: () => onSetDocumentTargetRow({ rowId: null }),
              }
            : null;

    const defaultMenuItems: (
        | string
        | {
              name: string;
              subMenu: {
                  name: string;
                  action?: () => void;
                  checked?: boolean;
              }[];
          }
    )[] = ["copy", "paste"];
    const additionalMenuItems = [
        ...(changeRowTypeSubMenu ? [changeRowTypeSubMenu] : []),
        ...(flipRowSignMenuItem ? [flipRowSignMenuItem] : []),
        ...(setDocumentTargetMenuItem ? [setDocumentTargetMenuItem] : []),
        ...(resetDocumentTargetMenuItem ? [resetDocumentTargetMenuItem] : []),
    ];

    return [
        ...defaultMenuItems,
        ...(additionalMenuItems.length > 0
            ? ["separator", ...additionalMenuItems]
            : []),
    ];
}
